import {
	IRegEdActionNeededItem,
	useRegEdAnswersLeftToAccountUser,
	RegEdActionReason,
	RegEdComplianceContentType,
	RegEdEmailKind,
	RegEdSubmissionFailureReason,
	useAccountUsesRegEd,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ILocationState } from '../../../../models';
import { BlogPostRequest } from '../../../../models/Blogs';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { getBlogPostAsync } from '../../../../queries';
import { IOperationResultNoValue } from '../../../../viewmodels/AppViewModels';
import { Button } from '../../../components-v2/Button';
import { Pill } from '../../../components-v2/Pill';
import { ThemeColors } from '../../../components-v2/Theme/theme';
import { GhostHeading } from '../../GhostHeading';
import { GhostImage } from '../../GhostImage';
import { GhostCard } from '../../GhostsCard';
import { HelpTooltip } from '../../HelpTooltip';
import { ChevronRight } from '../../svgs/icons/ChevronRight';
import { ComplianceShieldIcon } from '../../svgs/icons/ComplianceShieldIcon';
import { getRegEdFailureText } from '../../compliance/regEdFailureText';
import { DashboardCard, DashboardCardHeaderLabel, DashboardCardStat } from '../DashboardCard';
import { styleSheet } from './styles';

const EVENT_LOGGING_CATEGORY = 'DashboardComplianceActionNeeded';

interface IReasonPresentation {
	callToAction: string;
	detail: string;
	label: string;
	pillColor: keyof ThemeColors['pill'];
}

const getReasonPresentation = (item: IRegEdActionNeededItem, adminSession: boolean): IReasonPresentation | null => {
	switch (item.reason) {
		case RegEdActionReason.RevisionRequested:
			return {
				callToAction: 'Revise',
				detail: 'Your reviewer asked for changes before this can be approved.',
				label: 'Revisions requested',
				pillColor: 'red',
			};
		case RegEdActionReason.ConditionsPending:
			return {
				callToAction: 'Review conditions',
				detail: 'Approved once you complete the stated condition, then release it to send.',
				label: 'Approved with conditions',
				pillColor: 'orange',
			};
		case RegEdActionReason.SubmissionFailed:
			// Without a reason the approval request was never handed back, so there is nothing to resend from here.
			if (!item.failureReason) {
				return null;
			}
			if (item.failureReason === RegEdSubmissionFailureReason.NotSubmitted) {
				return {
					// The account's user answers these; an admin opening it finds them waiting, not a form.
					callToAction: adminSession ? 'View' : 'Submit for review',
					detail: adminSession
						? "It won't send until the account's user answers its compliance questions."
						: item.deferredDate
							? "This was scheduled for you. It won't send until you answer its compliance questions."
							: getRegEdFailureText(item.failureReason)!,
					label: 'Awaiting submission',
					pillColor: 'orange',
				};
			}
			return {
				callToAction: 'Resend',
				detail: getRegEdFailureText(item.failureReason) ?? 'This never reached a reviewer. Edit it and send it again.',
				label: 'Not filed',
				pillColor: 'red',
			};
		default:
			return null;
	}
};

const VISIBLE_ROW_LIMIT = 4;

// A card can hold campaigns, one-to-one sends and automation emails at once, and each lives under a different
// tab of the email report — so one link could only ever serve one of them. One button per kind present.
// `filtersStatus` mirrors the report's own per-tab filters: the Automations tab has no status filter and drops
// the param, so a link there must neither send one nor name one in its label.
const EMAIL_KIND_LINKS: { kind: RegEdEmailKind; noun: string; tab: Api.BulkEmailReportType; filtersStatus: boolean }[] =
	[
		{ filtersStatus: true, kind: RegEdEmailKind.Campaign, noun: 'Campaigns', tab: Api.BulkEmailReportType.Campaigns },
		{
			filtersStatus: true,
			kind: RegEdEmailKind.Individual,
			noun: 'Emails',
			tab: Api.BulkEmailReportType.IndividualEmails,
		},
		{
			filtersStatus: false,
			kind: RegEdEmailKind.Automation,
			noun: 'Automations',
			tab: Api.BulkEmailReportType.Automations,
		},
	];

const FILTERABLE_STATUSES: readonly string[] = [Api.EmailSendStatus.Rejected, Api.EmailSendStatus.WaitingForApproval];

/**
 * Filters on every status the items carry, so none of them drop out of their own link. Anything that could
 * hide one — an item past the server's cap, or a status the report cannot filter on — opens the tab
 * unfiltered instead, and the label names a status only when it's true of every item.
 */
const emailDestination = (
	items: IRegEdActionNeededItem[],
	noun: string,
	tab: Api.BulkEmailReportType | undefined,
	filtersStatus: boolean
) => {
	const statuses = [...new Set(items.map(item => item.parentStatus))];
	const filterable = statuses.length > 0 && statuses.every(status => !!status && FILTERABLE_STATUSES.includes(status));
	const filtered = filtersStatus && filterable ? statuses : null;
	const query = [tab ? `tab=${tab}` : null, filtered ? `status=${filtered.join(',')}` : null].filter(Boolean).join('&');
	const allRejected = filtered?.length === 1 && filtered[0] === Api.EmailSendStatus.Rejected;
	return {
		label: allRejected ? `View Rejected ${noun}` : `View ${noun}`,
		path: `/reporting/group-email${query ? `?${query}` : ''}`,
	};
};

export interface IComplianceReportingDestination {
	key: string;
	label: string;
	path: string;
}

/** Blog has no reporting page yet, so it is deliberately absent rather than linked nowhere. */
export const complianceReportingDestinations = (
	items: IRegEdActionNeededItem[],
	{ complete = true }: { complete?: boolean } = {}
): IComplianceReportingDestination[] => {
	const emails = items.filter(item => item.contentType === RegEdComplianceContentType.Email);
	const byKind = EMAIL_KIND_LINKS.filter(link => emails.some(item => item.emailKind === link.kind)).map(link => ({
		key: link.kind,
		...emailDestination(
			emails.filter(item => item.emailKind === link.kind),
			link.noun,
			link.tab,
			complete && link.filtersStatus
		),
	}));
	// An item with no kind still needs a way out, so keep one link rather than dropping it.
	const untyped =
		!byKind.length && emails.length
			? [{ key: 'Email', ...emailDestination(emails, 'Emails', undefined, complete) }]
			: [];
	// Social cannot carry a status yet: the report filters on `ReportPostStatus`, which the item's own status
	// is not — so the label does not promise a filter the link cannot apply.
	const social = items.some(item => item.contentType === RegEdComplianceContentType.SocialMediaPost)
		? [{ key: 'SocialMediaPost', label: 'View all social posts', path: '/reporting/social-media-posts' }]
		: [];
	return [...byKind, ...untyped, ...social];
};

const keyOf = (item: IRegEdActionNeededItem): string => item.submissionId ?? item.parentId!;

/** The first row opens by default, so the card shows what it is asking for without a click. */
const firstKeyOf = (items?: IRegEdActionNeededItem[]): string[] => {
	const first = (items ?? []).find(item => !!item.parentId);
	return first ? [keyOf(first)] : [];
};

// `Unknown` is a real member of the server's enum, and a content type this version cannot route has
// nowhere to send the advisor — so the item is dropped rather than shown behind a button that does nothing.
const isRoutableContentType = (contentType?: RegEdComplianceContentType): boolean =>
	contentType === RegEdComplianceContentType.Email ||
	contentType === RegEdComplianceContentType.SocialMediaPost ||
	contentType === RegEdComplianceContentType.Blog;

const getScheduleText = (scheduledDate?: string | null): { date: string; isOverdue: boolean; label: string } | null => {
	if (!scheduledDate) {
		return null;
	}
	const parsed = new Date(scheduledDate);
	if (Number.isNaN(parsed.getTime())) {
		return null;
	}
	const isOverdue = parsed.getTime() < Date.now();
	return {
		date: getDefaultDateStringValue(scheduledDate),
		isOverdue,
		label: isOverdue ? 'Overdue' : 'Scheduled for',
	};
};

interface IProps {
	/** Absent when the account is off RegEd or owes nothing — the server omits the card entirely. */
	card?: Api.IComplianceActionNeededDashboardCard;
	status?: 'error' | 'success' | 'pending';
}

/**
 * The advisor's compliance to-do list. Separate from the campaign and calendar views on purpose: a
 * decision often lands after the chosen send date, and those views omit held and rejected pieces, so
 * work still owed can be invisible everywhere else.
 */
export const DashboardComplianceActionNeededCard: FC<IProps> = ({ card, status }) => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging(EVENT_LOGGING_CATEGORY);
	const history = useHistory();
	const impersonationContext = userSession?.impersonationContext;
	// The dashboard fetches every card in one request, so a pending state says nothing about whether this
	// account is on RegEd — without this gate an account that isn't would flash a compliance skeleton.
	const usesRegEd = useAccountUsesRegEd(impersonationContext);
	const { isLoading: whoAnswersLoading, leftToAccountUser: adminSession } =
		useRegEdAnswersLeftToAccountUser(impersonationContext);
	const items = card?.values;
	const [openingParentId, setOpeningParentId] = useState<string | null>(null);
	// Keyed rather than indexed so filtering an item out doesn't shift which rows are open.
	const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(firstKeyOf(card?.values)));

	const toggle = (key: string) =>
		setExpanded(previous => {
			const next = new Set(previous);
			if (!next.delete(key)) {
				next.add(key);
			}
			return next;
		});

	const openPiece = async (item: IRegEdActionNeededItem) => {
		if (!item.parentId) {
			return;
		}
		logEvent('OpenActionNeededItem', { contentType: item.contentType, reason: item.reason });
		setOpeningParentId(item.parentId);
		try {
			switch (item.contentType) {
				case RegEdComplianceContentType.Email: {
					// Without this the load resolves against the admin's own account, not the impersonated one.
					const campaign = new Api.CampaignViewModel(userSession, { id: item.parentId } as Api.ICampaign).impersonate(
						impersonationContext
					);
					await campaign.load();
					const state: ILocationState<Api.CampaignViewModel, any> = { viewModel: campaign };
					fullscreenModal?.history?.push({ pathname: '/email/campaigns/edit', state });
					break;
				}
				case RegEdComplianceContentType.SocialMediaPost: {
					const post = new Api.SocialMediaPostViewModel(userSession, { id: item.parentId }).impersonate(
						impersonationContext
					);
					await post.load();
					const state: ILocationState<Api.SocialMediaPostViewModel, any> = { viewModel: post };
					fullscreenModal?.history?.push({
						pathname: `/social-media/post/edit-post-draft/${item.parentId}`,
						state,
					});
					break;
				}
				case RegEdComplianceContentType.Blog: {
					const post = await getBlogPostAsync({ id: item.parentId, impersonationContext, userSession });
					const state: ILocationState<any, BlogPostRequest> = { model: { post } };
					fullscreenModal?.history?.push({ pathname: `/blog/post/edit/${item.parentId}`, state });
					break;
				}
				default:
					break;
			}
		} catch (error) {
			logApiError('OpenActionNeededItem-Error', error as IOperationResultNoValue);
			errorMessages.pushApiError(error);
		} finally {
			setOpeningParentId(null);
		}
	};

	// An unrecognized reason has no known advisor action, so it must not be offered one. Nor an
	// unroutable content type, which has no destination to offer.
	const actionable = (items ?? []).filter(
		item => !!item.parentId && !!getReasonPresentation(item, adminSession) && isRoutableContentType(item.contentType)
	);

	const visible = actionable.slice(0, VISIBLE_ROW_LIMIT);

	const toggleAll = (collapse: boolean) => setExpanded(collapse ? new Set<string>() : new Set(visible.map(keyOf)));

	// The rows arrive with the dashboard, ahead of the integration, so an admin's actions wait until it says who answers.
	if (whoAnswersLoading) {
		return null;
	}

	if (usesRegEd && !card && status === 'pending') {
		return (
			<GhostCard stylesDeclarations={[styleSheet.ghost]}>
				<div className={css(styleSheet.header)}>
					<GhostImage />
					<div className={css(styleSheet.headerGroup)}>
						<GhostHeading style={{ height: 10, width: 180 }} />
						<GhostHeading style={{ height: 24, width: 32 }} />
					</div>
				</div>
				<GhostHeading style={{ height: 36, width: '100%' }} />
				<GhostHeading style={{ height: 36, width: '100%' }} />
			</GhostCard>
		);
	}

	if (!actionable.length) {
		return null;
	}

	// `values` is capped server-side, so anything past the cap is only visible through `total`. Measured
	// against what the server sent, not what survived filtering — an unrecognized reason was dropped on
	// purpose and isn't waiting behind the cap.
	const dropped = (items?.length ?? 0) - actionable.length;
	const notShown = Math.max((card?.total ?? actionable.length) - dropped - actionable.length, 0);

	const total = actionable.length + notShown;

	// Anything the card is holding back, whether this component capped it or the server did.
	const hasMore = actionable.length > visible.length || notShown > 0;
	const destinations = complianceReportingDestinations(actionable, { complete: notShown === 0 });

	const allExpanded = visible.every(item => expanded.has(keyOf(item)));

	return (
		<DashboardCard styleDeclaration={styleSheet.card}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.iconTile)}>
					<ComplianceShieldIcon aria-hidden />
				</div>
				<div className={css(styleSheet.headerGroup)}>
					<div className={css(styleSheet.headerLabelRow)}>
						<DashboardCardHeaderLabel styleDeclaration={styleSheet.headerContent}>
							Compliance Action Required
						</DashboardCardHeaderLabel>
						<HelpTooltip styles={[styleSheet.headerHelp]}>
							<span className={css(styleSheet.headerHelpText)}>
								Your compliance team responded to these, or they could not be filed. They will not send until you act on
								them.
							</span>
						</HelpTooltip>
					</div>
					<DashboardCardStat styleDeclaration={styleSheet.count}>{total}</DashboardCardStat>
				</div>
				<Button variant='link' size='sm' styles={[styleSheet.toggleAll]} onClick={() => toggleAll(allExpanded)}>
					{allExpanded ? 'Collapse all' : 'Expand all'}
				</Button>
			</div>

			<div className={css(styleSheet.divider)} />

			<div className={css(styleSheet.list)}>
				{visible.map(item => {
					const presentation = getReasonPresentation(item, adminSession)!;
					const schedule = getScheduleText(item.scheduledDate);
					const reviewNote = item.reviewNotes?.find(note => !!note.noteText?.trim())?.noteText?.trim();
					const key = keyOf(item);
					const isExpanded = expanded.has(key);
					return (
						<div className={css(styleSheet.item)} key={key}>
							<div className={css(styleSheet.itemHeader)}>
								<button
									type='button'
									className={css(styleSheet.itemToggle)}
									aria-expanded={isExpanded}
									onClick={() => toggle(key)}
								>
									<ChevronRight
										width={16}
										height={16}
										className={css(styleSheet.caret, isExpanded ? styleSheet.caretOpen : null)}
										aria-hidden
									/>
									<h6 className={css(styleSheet.itemTitle)}>{item.title || 'Untitled'}</h6>
								</button>
								<div className={css(styleSheet.statusColumn)}>
									<Pill variant='label' color={presentation.pillColor} label={presentation.label} />
								</div>
								<div className={css(styleSheet.dateColumn)}>
									{schedule ? (
										<>
											<span>{schedule.label}</span>
											<span className={css(schedule.isOverdue ? styleSheet.overdue : null)}>{schedule.date}</span>
										</>
									) : null}
								</div>
								<div className={css(styleSheet.actionColumn)}>
									<Button
										variant='secondary'
										size='sm'
										styles={[styleSheet.itemAction]}
										disabled={openingParentId === item.parentId}
										isLoading={openingParentId === item.parentId}
										onClick={() => openPiece(item)}
									>
										{presentation.callToAction}
									</Button>
								</div>
							</div>
							{isExpanded ? (
								<div className={css(styleSheet.itemBody)}>
									<div className={css(styleSheet.itemDetail)}>{presentation.detail}</div>
									{reviewNote ? <div className={css(styleSheet.itemNote)}>{reviewNote}</div> : null}
									{item.trackingNumber ? (
										<div className={css(styleSheet.itemTracking)}>Tracking #: {item.trackingNumber}</div>
									) : null}
								</div>
							) : null}
						</div>
					);
				})}
			</div>
			{hasMore ? (
				<div className={css(styleSheet.viewAllRow)}>
					<span className={css(styleSheet.showingCount)}>
						Showing {visible.length} of {total}
					</span>
					{destinations.map(destination => (
						<Button key={destination.key} variant='link' size='sm' onClick={() => history.push(destination.path)}>
							{destination.label}
						</Button>
					))}
				</div>
			) : null}
		</DashboardCard>
	);
};
